import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowLeft, Loader2, AlertTriangle, Car, Building2, Headphones, Star, CheckCircle, BookOpen, Wrench, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const SP_SHORT = 'A trusted marketplace connecting transportation businesses with verified service partners — compliance, ELDs, insurance, factoring, training, maintenance, and more.';
const SP_LONG = 'This provider marketplace connects box truck owners, owner-operators, carriers, and transportation businesses with trusted partners that support every stage of business growth. From FMCSA compliance, DOT regulations, ELD solutions, insurance coverage, and safety management to load boards, factoring, financing, bookkeeping, payroll, tax services, and business consulting, members gain access to essential resources designed to streamline operations and increase profitability. The marketplace also features training providers, maintenance and repair services, roadside assistance, and employee benefit solutions, creating a one-stop destination for building, managing, and scaling a successful transportation business.';

const ROLES = [
  { value: 'driver', label: 'Driver', icon: Car, desc: 'Independent driver looking for loads, routes, and career growth opportunities.', bg: 'bg-blue-600', bgLight: 'bg-blue-50', border: 'border-blue-600', ring: 'ring-blue-600', text: 'text-blue-700', iconActive: 'text-white', iconIdle: 'text-blue-500' },
  { value: 'carrier_owner', label: 'Carrier / Owner', icon: Building2, desc: 'Fleet owner or authority holder managing drivers, compliance, and operations.', bg: 'bg-emerald-600', bgLight: 'bg-emerald-50', border: 'border-emerald-600', ring: 'ring-emerald-600', text: 'text-emerald-700', iconActive: 'text-white', iconIdle: 'text-emerald-500' },
  { value: 'dispatcher', label: 'Dispatcher', icon: Headphones, desc: 'Dispatch service provider connecting carriers with freight and logistics support.', bg: 'bg-violet-600', bgLight: 'bg-violet-50', border: 'border-violet-600', ring: 'ring-violet-600', text: 'text-violet-700', iconActive: 'text-white', iconIdle: 'text-violet-500' },
  { value: 'learner', label: 'Learn the Trade', icon: BookOpen, desc: 'Gain insight into box truck industry with guidance, education, and mentorship to build a successful career.', bg: 'bg-amber-600', bgLight: 'bg-amber-50', border: 'border-amber-600', ring: 'ring-amber-600', text: 'text-amber-700', iconActive: 'text-white', iconIdle: 'text-amber-500' },
  { value: 'service_provider', label: 'Service Provider', icon: Wrench, desc: SP_SHORT, longDesc: SP_LONG, bg: 'bg-sky-600', bgLight: 'bg-sky-50', border: 'border-sky-600', ring: 'ring-sky-600', text: 'text-sky-700', iconActive: 'text-white', iconIdle: 'text-sky-500' },
];

const JOIN_SIDE_IMAGE = 'https://images.unsplash.com/photo-1588758167582-5c1021c30e0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxtb3ZpbmclMjB0cnVjayUyMGZyZWlnaHQlMjB2YW4lMjBjb21tZXJjaWFsJTIwZHJpdmluZ3xlbnwwfHx8fDE3NzYzMTM1Njh8MA&ixlib=rb-4.1.0&q=85';

const Join = () => {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', role: 'driver', referral_code: '' });
  const [spExpanded, setSpExpanded] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/verification', { replace: true });
  }, [user, navigate]);

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, form.role, form.referral_code || undefined);
      toast.success('Welcome to StraightConnect!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Registration failed. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Side Image */}
      <div className="hidden lg:block lg:w-[38%] relative">
        <img src={JOIN_SIDE_IMAGE} alt="Logistics fleet" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-blue-900/40 to-slate-900/20" />
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <div className="flex items-center gap-2.5 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
            <span className="text-white text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
          </div>
          <h2 className="text-2xl font-bold text-white leading-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Your business starts here.</h2>
          <p className="text-slate-300 text-sm mb-4">Join 500+ non-CDL carriers scaling smarter with StraightConnect.</p>
          <div className="space-y-2 mt-4">
            {['Real-time DOT & FMCSA compliance', 'Verified carrier network access', 'Job board & dispatch matching', 'Mentorship & growth resources'].map(item => (
              <div key={item} className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                <span className="text-slate-200 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-10 bg-white overflow-y-auto">
        <div className="w-full max-w-[500px]">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-8 transition-colors" data-testid="join-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <div className="lg:hidden flex items-center gap-2.5 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Create your account</h2>
          <p className="text-slate-500 text-sm mb-8">Join the verified logistics network</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 mb-6 flex items-center gap-2.5 text-sm" data-testid="join-error">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="join-form">
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">Full name</label>
              <input type="text" required value={form.name} onChange={update('name')} placeholder="Your full name"
                className="w-full h-11 px-4 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent" data-testid="join-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">Email address</label>
              <input type="email" required value={form.email} onChange={update('email')} placeholder="you@company.com" autoComplete="email"
                className="w-full h-11 px-4 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent" data-testid="join-email-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} required value={form.password} onChange={update('password')} placeholder="Min 6 characters" autoComplete="new-password"
                  className="w-full h-11 px-4 pr-11 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent" data-testid="join-password-input" />
                <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5" data-testid="join-toggle-password">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">Confirm password</label>
              <input type={showPw ? 'text' : 'password'} required value={form.confirmPassword} onChange={update('confirmPassword')} placeholder="Re-enter password" autoComplete="new-password"
                className="w-full h-11 px-4 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent" data-testid="join-confirm-password-input" />
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-3">I am a...</label>
              <div className="space-y-3">
                {ROLES.map(r => {
                  const isActive = form.role === r.value;
                  const isSP = r.value === 'service_provider';
                  return (
                    <button type="button" key={r.value} onClick={() => setForm(prev => ({ ...prev, role: r.value }))}
                      className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${isActive ? `${r.border} ${r.bgLight} ring-1 ${r.ring}` : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                      data-testid={`join-role-${r.value}`}>
                      <div className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${isActive ? r.bg : 'bg-slate-100'}`}>
                        <r.icon className={`h-5 w-5 ${isActive ? r.iconActive : r.iconIdle}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm font-semibold block ${isActive ? r.text : 'text-slate-800'}`}>{r.label}</span>
                        <span className={`text-xs block mt-0.5 leading-relaxed ${isActive ? 'text-slate-600' : 'text-slate-400'}`}>
                          {isSP && !spExpanded ? r.desc : (isSP ? r.longDesc : r.desc)}
                        </span>
                        {isSP && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); setSpExpanded(v => !v); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setSpExpanded(v => !v); } }}
                            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-700 cursor-pointer select-none"
                            data-testid="sp-read-more-toggle">
                            {spExpanded ? <>Read less <ChevronUp className="h-3 w-3" /></> : <>Read more <ChevronDown className="h-3 w-3" /></>}
                          </span>
                        )}
                      </div>
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center ${isActive ? `${r.border} ${r.bg}` : 'border-slate-300'}`}>
                        {isActive && <CheckCircle className="h-3 w-3 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">Referral code <span className="text-slate-400 font-normal">(optional)</span></label>
              <div className="relative">
                <Star className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="text" value={form.referral_code} onChange={update('referral_code')} placeholder="Enter code"
                  className="w-full h-11 pl-10 pr-4 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent" data-testid="join-referral-input" />
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 mt-2" data-testid="join-submit-button">
              {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Creating account...</> : 'Create account'}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline underline-offset-4" data-testid="join-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Join;
